// ============================================================
// CustomerPicker.jsx  —  Noble Enterprises (tape module)
//
// One field that is both a search box and a dropdown, for when
// there are too many customers to scroll a plain list.
//
//   Typing   "a" lists every name starting with A, A to Z. From
//            two letters on, names that only contain the text
//            ("Haji Ali Store" for "ali") come after them. Phone numbers match too, on
//            digits only, so "0300 76" finds "0300-7671404".
//   Empty    Recently billed customers first (the same shops order
//            again and again), then everyone A to Z.
//   Keys     Up / Down to move, Enter to pick, Esc to close.
//
// Props
//   customers    active customers (rows from /tape/sales/customers)
//   value        the chosen customer id, or ''
//   onChange     called with the new id ('' when cleared)
//   showBalance  show balances in the list — owner only
// ============================================================

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, X, Search } from 'lucide-react';

const C = {
  brand:      '#f39c12',
  text:       '#1a2332',
  textSoft:   '#64748b',
  textMuted:  '#94a3b8',
  border:     '#e2e8f0',
  borderSoft: '#f1f5f9',
  rowAlt:     '#fafbfc',
  hover:      '#fff7e6',
  danger:     '#dc2626',
  info:       '#0284c7',
};

const RECENT_COUNT = 8;
const ROW_HEIGHT   = 44;   // px; the panel shows about 8 rows

const num    = (n) => Number(n || 0);
const money  = (n) => `Rs ${num(n).toLocaleString('en-PK', { maximumFractionDigits: 0 })}`;
const digits = (s) => String(s || '').replace(/\D/g, '');
const byName = (a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' });

const balanceText = (b) =>
  num(b) >= 1 ? `${money(b)} DR` : num(b) <= -1 ? `${money(Math.abs(num(b)))} CR` : 'Settled';
const balanceColour = (b) =>
  num(b) >= 1 ? C.danger : num(b) <= -1 ? C.info : C.textMuted;

const CustomerPicker = ({ customers, value, onChange, showBalance = false }) => {
  const [open, setOpen]     = useState(false);
  const [query, setQuery]   = useState('');
  const [active, setActive] = useState(0);
  const wrapRef  = useRef(null);
  const inputRef = useRef(null);
  const listRef  = useRef(null);

  const selected = customers.find(c => String(c.id) === String(value)) || null;

  // The rows the panel shows, as a flat list with section labels
  // between them. Only 'customer' rows can be highlighted or picked.
  const rows = useMemo(() => {
    const term = query.trim().toLowerCase();
    const sorted = [...customers].sort(byName);

    if (!term) {
      const recent = customers
        .filter(c => c.last_invoice_date)
        .sort((a, b) => String(b.last_invoice_date).localeCompare(String(a.last_invoice_date)) || byName(a, b))
        .slice(0, RECENT_COUNT);
      const out = [];
      if (recent.length > 0) {
        out.push({ kind: 'label', text: 'Recently billed' });
        recent.forEach(c => out.push({ kind: 'customer', c, key: `r${c.id}` }));
        out.push({ kind: 'label', text: 'All customers A–Z' });
      }
      sorted.forEach(c => out.push({ kind: 'customer', c, key: `a${c.id}` }));
      return out;
    }

    const termDigits = digits(term);
    const starts = [];
    const contains = [];
    sorted.forEach(c => {
      const name = c.name.toLowerCase();
      if (name.startsWith(term)) starts.push(c);
      // One letter means "names starting with it" and nothing more;
      // from two letters on, names containing the text come too.
      else if ((term.length >= 2 && name.includes(term))
        || (termDigits.length >= 3 && digits(c.phone).includes(termDigits))) contains.push(c);
    });
    return [...starts, ...contains].map(c => ({ kind: 'customer', c, key: `s${c.id}` }));
  }, [customers, query]);

  const pickable = rows.filter(r => r.kind === 'customer');

  // Highlight the first match whenever the list changes
  useEffect(() => { setActive(0); }, [query, open]);

  // Keep the highlighted row in view while arrowing through the list
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-pick="${active}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  // A click anywhere outside closes the panel and drops half-typed text
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const pick = (c) => {
    onChange(String(c.id));
    setQuery('');
    setOpen(false);
  };

  const clear = () => {
    onChange('');
    setQuery('');
    setOpen(true);
    if (inputRef.current) inputRef.current.focus();
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setActive(a => Math.min(a + 1, pickable.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(a => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      // Never submits the form from here
      e.preventDefault();
      if (open && pickable[active]) pick(pickable[active].c);
    } else if (e.key === 'Escape') {
      if (open) { e.stopPropagation(); setOpen(false); setQuery(''); }
    } else if (e.key === 'Tab') {
      setOpen(false);
      setQuery('');
    }
  };

  // While the panel is open the box shows what is being typed;
  // closed, it shows the chosen name.
  const shown = open ? query : (selected ? selected.name : '');

  let pickIndex = -1;

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <Search
          size={15}
          style={{
            position: 'absolute', left: '12px', top: '50%',
            transform: 'translateY(-50%)', color: C.textMuted, pointerEvents: 'none',
          }}
        />
        <input
          ref={inputRef}
          value={shown}
          placeholder={selected && open ? selected.name : 'Type a name or phone…'}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onKeyDown={onKeyDown}
          autoComplete="off"
          spellCheck={false}
          style={{
            width: '100%', padding: '10px 62px 10px 34px',
            border: `1px solid ${open ? C.brand : C.border}`, borderRadius: '8px',
            fontSize: '14px', color: C.text, boxSizing: 'border-box',
            fontFamily: 'inherit', background: '#ffffff', outline: 'none',
            fontWeight: selected && !open ? '600' : '400',
          }}
        />
        <div style={{
          position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)',
          display: 'flex', alignItems: 'center', gap: '2px',
        }}>
          {selected && (
            <button
              type="button"
              title="Clear"
              onMouseDown={e => e.preventDefault()}
              onClick={clear}
              style={iconBtn}
            >
              <X size={15} />
            </button>
          )}
          <button
            type="button"
            title={open ? 'Close list' : 'Show all customers'}
            onMouseDown={e => e.preventDefault()}
            onClick={() => {
              if (open) { setOpen(false); setQuery(''); }
              else { setOpen(true); if (inputRef.current) inputRef.current.focus(); }
            }}
            style={iconBtn}
          >
            <ChevronDown
              size={16}
              style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
            />
          </button>
        </div>
      </div>

      {open && (
        <div
          ref={listRef}
          style={{
            position: 'absolute', left: 0, right: 0, top: 'calc(100% + 4px)', zIndex: 50,
            background: '#ffffff', border: `1px solid ${C.border}`, borderRadius: '8px',
            boxShadow: '0 10px 28px rgba(15, 23, 42, 0.14)',
            maxHeight: `${ROW_HEIGHT * 8}px`, overflowY: 'auto',
            minWidth: '300px',
          }}
        >
          {pickable.length === 0 && (
            <div style={{ padding: '14px', fontSize: '13px', color: C.textMuted }}>
              {customers.length === 0
                ? 'No active customers. Add one on the Customers screen.'
                : `No customer matches "${query.trim()}".`}
            </div>
          )}

          {rows.map((r, i) => {
            if (r.kind === 'label') {
              return (
                <div key={`l${i}`} style={{
                  padding: '8px 12px 5px', fontSize: '10px', fontWeight: '700',
                  color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px',
                  background: C.rowAlt, borderTop: i > 0 ? `1px solid ${C.borderSoft}` : 'none',
                }}>
                  {r.text}
                </div>
              );
            }
            pickIndex += 1;
            const idx = pickIndex;
            const isActive = idx === active;
            const isChosen = selected && String(selected.id) === String(r.c.id);
            return (
              <div
                key={r.key}
                data-pick={idx}
                onMouseDown={e => e.preventDefault()}
                onClick={() => pick(r.c)}
                onMouseEnter={() => setActive(idx)}
                style={{
                  minHeight: `${ROW_HEIGHT}px`, boxSizing: 'border-box',
                  padding: '7px 12px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '10px',
                  background: isActive ? C.hover : '#ffffff',
                  borderLeft: `3px solid ${isChosen ? C.brand : 'transparent'}`,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '14px', fontWeight: '600', color: C.text,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {r.c.name}
                  </div>
                  {r.c.phone && (
                    <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '1px' }}>
                      {r.c.phone}
                    </div>
                  )}
                </div>
                {showBalance && (
                  <div style={{
                    fontSize: '12px', fontWeight: '700', whiteSpace: 'nowrap',
                    color: balanceColour(r.c.balance),
                  }}>
                    {balanceText(r.c.balance)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// The strip under the field once a customer is chosen, so the
// operator sees what is pending before billing more.
export const CustomerStrip = ({ customer, showBalance = false }) => {
  if (!customer) return null;
  const terms = customer.payment_type === 'credit'
    ? `Credit · ${num(customer.payment_terms_days)} days`
    : 'Debit (immediate)';
  const last = customer.last_invoice_date
    ? new Date(customer.last_invoice_date).toLocaleDateString('en-GB')
    : 'Never billed';

  const item = (label, val, colour = C.text) => (
    <div>
      <div style={{ fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
        {label}
      </div>
      <div style={{ fontSize: '14px', fontWeight: '700', color: colour, marginTop: '2px' }}>{val}</div>
    </div>
  );

  return (
    <div style={{
      display: 'flex', gap: '28px', flexWrap: 'wrap',
      padding: '11px 16px', marginBottom: '20px',
      background: C.rowAlt, border: `1px solid ${C.borderSoft}`, borderRadius: '8px',
    }}>
      {showBalance && item('Outstanding', balanceText(customer.balance), balanceColour(customer.balance))}
      {item('Terms', terms)}
      {item('Last invoice', last, customer.last_invoice_date ? C.text : C.textMuted)}
      {customer.phone && item('Phone', customer.phone)}
    </div>
  );
};

const iconBtn = {
  width: '28px', height: '28px', border: 'none', background: 'transparent',
  color: C.textMuted, cursor: 'pointer', borderRadius: '6px',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};

export default CustomerPicker;